import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import Segmented from '@/components/base/Segmented';
import Modal from '@/components/base/Modal';
import { getOfferStatus, OFFER_STATUS_META } from '@/lib/offers';
import { logActivity } from '@/lib/activityLog';
import type { Offer, OfferEventType, OfferProduct } from '@/lib/types';
import OfferForm, { type OfferWithProducts } from '@/pages/offers/components/OfferForm';
import OfferHistory from '@/pages/offers/components/OfferHistory';

type Tab = 'list' | 'history';
type Filter = 'todas' | 'activas' | 'programadas' | 'finalizadas';

export default function OffersPage() {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState<Tab>('list');
  const [filter, setFilter] = useState<Filter>('todas');
  const [offers, setOffers] = useState<OfferWithProducts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OfferWithProducts | null>(null);
  const [confirm, setConfirm] = useState<{
    type: 'edit' | 'delete' | 'toggle';
    offer: OfferWithProducts;
  } | null>(null);

  const userName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    user?.email ||
    'Desconocido';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: offersData, error: offersErr }, { data: productsData }] = await Promise.all([
        supabase.from('offers').select('*').order('created_at', { ascending: false }),
        supabase.from('offer_products').select('*'),
      ]);
      if (offersErr) throw offersErr;
      const products = (productsData as OfferProduct[]) ?? [];
      const map = new Map<string, string[]>();
      for (const op of products) {
        const list = map.get(op.offer_id) ?? [];
        list.push(op.product_id);
        map.set(op.offer_id, list);
      }
      setOffers(
        ((offersData as Offer[]) ?? []).map((o) => ({
          ...o,
          productIds: map.get(o.id) ?? [],
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las ofertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const logEvent = useCallback(
    async (type: OfferEventType, offerId: string, details?: Record<string, unknown>) => {
      await supabase.from('offer_events').insert({
        offer_id: offerId,
        event_type: type,
        user_id: user?.id ?? null,
        user_name: userName,
        details: details ?? null,
      });
      void logActivity({
        module: 'offers',
        action: `offer_${type}`,
        description: `Oferta: ${type}`,
        targetType: 'offer',
        targetId: offerId,
        metadata: details ?? undefined,
      });
    },
    [user, userName]
  );

  const filtered = useMemo(() => {
    return offers.filter((o) => {
      if (!o.active) return filter === 'todas';
      const status = getOfferStatus(o);
      if (filter === 'activas') return status === 'activa';
      if (filter === 'programadas') return status === 'programada';
      if (filter === 'finalizadas') return status === 'finalizada';
      return true;
    });
  }, [offers, filter]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (offer: OfferWithProducts) => {
    if (offer.active && getOfferStatus(offer) === 'activa') {
      setConfirm({ type: 'edit', offer });
      return;
    }
    setEditing(offer);
    setFormOpen(true);
  };

  const confirmEdit = async () => {
    if (!confirm) return;
    setEditing(confirm.offer);
    setFormOpen(true);
    setConfirm(null);
  };

  const handleToggle = async (offer: OfferWithProducts) => {
    const next = !offer.active;
    const { error: e } = await supabase
      .from('offers')
      .update({ active: next, updated_at: new Date().toISOString() })
      .eq('id', offer.id);
    if (e) return;
    await logEvent(next ? 'activated' : 'cancelled', offer.id, { name: offer.name });
    load();
  };

  const confirmToggle = () => {
    if (confirm) handleToggle(confirm.offer);
    setConfirm(null);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    const { error: e } = await supabase.from('offers').delete().eq('id', confirm.offer.id);
    if (e) return;
    await logEvent('cancelled', confirm.offer.id, { name: confirm.offer.name, deleted: true });
    setConfirm(null);
    load();
  };

  const requestDelete = (offer: OfferWithProducts) => {
    setConfirm({ type: 'delete', offer });
  };

  const requestToggle = (offer: OfferWithProducts) => {
    setConfirm({ type: 'toggle', offer });
  };

  const filterOptions = [
    { value: 'todas', label: 'Todas' },
    { value: 'activas', label: 'Activas' },
    { value: 'programadas', label: 'Programadas' },
    { value: 'finalizadas', label: 'Finalizadas' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Ofertas</h1>
          <p className="mt-1 text-foreground-600">
            Ofertas por producto con fecha y hora de inicio/finalización. Se aplican automáticamente.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-add-line" /> Crear oferta
        </button>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <div className="overflow-x-auto">
          <Segmented
            options={[
              { value: 'list', label: 'Ofertas' },
              { value: 'history', label: 'Historial' },
            ]}
            value={tab}
            onChange={(v) => setTab(v as Tab)}
          />
        </div>

        {tab === 'list' && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {filterOptions.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value as Filter)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
                    filter === f.value
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
                <i className="ri-price-tag-3-line text-4xl text-foreground-400" />
                <p className="mt-3 text-foreground-600">No hay ofertas en esta categoría.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-background-100 text-foreground-600 text-left">
                    <tr>
                      <th className="px-4 py-3 font-medium">Oferta</th>
                      <th className="px-4 py-3 font-medium text-center">Productos</th>
                      <th className="px-4 py-3 font-medium">Descuento</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Inicio</th>
                      <th className="px-4 py-3 font-medium hidden lg:table-cell">Finaliza</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="px-4 py-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {filtered.map((o) => {
                      const status = o.active ? getOfferStatus(o) : null;
                      const meta = status ? OFFER_STATUS_META[status] : null;
                      return (
                        <tr key={o.id} className="hover:bg-background-50">
                          <td className="px-4 py-3 font-medium text-foreground-950">{o.name}</td>
                          <td className="px-4 py-3 text-center text-foreground-700">
                            {o.productIds.length}
                          </td>
                          <td className="px-4 py-3 text-accent-700 font-semibold">
                            -{Number(o.percentage)}%
                          </td>
                          <td className="px-4 py-3 text-foreground-600 hidden lg:table-cell">
                            {new Date(o.starts_at).toLocaleString('es-DO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 hidden lg:table-cell">
                            {new Date(o.ends_at).toLocaleString('es-DO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3">
                            {!o.active ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-foreground-100 text-foreground-700">
                                <i className="ri-pause-circle-line" /> Desactivada
                              </span>
                            ) : (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta?.badge}`}
                              >
                                <i className={meta?.icon} /> {meta?.label}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => requestToggle(o)}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                                title={o.active ? 'Desactivar' : 'Activar'}
                              >
                                <i className={o.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                              </button>
                              <button
                                onClick={() => openEdit(o)}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                                title="Editar"
                              >
                                <i className="ri-edit-line" />
                              </button>
                              <button
                                onClick={() => requestDelete(o)}
                                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-accent-50 hover:text-accent-700 cursor-pointer"
                                title="Eliminar"
                              >
                                <i className="ri-delete-bin-6-line" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === 'history' && <OfferHistory />}
      </div>

      <OfferForm
        open={formOpen}
        editing={editing}
        allOffers={offers}
        onClose={() => setFormOpen(false)}
        onSaved={(offerId, offerName) => {
          setFormOpen(false);
          logEvent(editing ? 'updated' : 'created', offerId, { name: offerName }).finally(() =>
            load()
          );
        }}
      />

      {/* Confirmación */}
      <Modal
        open={confirm != null}
        onClose={() => setConfirm(null)}
        title={
          confirm?.type === 'edit'
            ? 'La oferta está activa'
            : confirm?.type === 'delete'
              ? 'Eliminar oferta'
              : 'Cambiar estado'
        }
        footer={
          <>
            <button
              onClick={() => setConfirm(null)}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={
                confirm?.type === 'edit'
                  ? confirmEdit
                  : confirm?.type === 'delete'
                    ? handleDelete
                    : confirmToggle
              }
              className="rounded-md bg-accent-500 hover:bg-accent-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
            >
              {confirm?.type === 'edit' ? 'Editar de todos modos' : 'Confirmar'}
            </button>
          </>
        }
      >
        {confirm?.type === 'edit' && (
          <p className="text-foreground-700">
            La oferta <span className="font-medium text-foreground-950">{confirm.offer.name}</span> está
            activa en este momento. ¿Seguro que quieres modificarla?
          </p>
        )}
        {confirm?.type === 'delete' && (
          <p className="text-foreground-700">
            ¿Eliminar la oferta <span className="font-medium text-foreground-950">{confirm.offer.name}</span>?
            Esta acción no se puede deshacer.
          </p>
        )}
        {confirm?.type === 'toggle' && (
          <p className="text-foreground-700">
            ¿{confirm.offer.active ? 'Desactivar' : 'Activar'} la oferta{' '}
            <span className="font-medium text-foreground-950">{confirm.offer.name}</span>?
          </p>
        )}
      </Modal>
    </div>
  );
}